import React, { useState } from 'react';
import moment from 'moment';
import { FiClock } from 'react-icons/fi';
import ShieldCheckIcon from '../ShieldCheckIcon';
import { AccountSecurityResult, AccountDeviceView } from '../../../model/AccountSecurityResult';
import ConfirmEx from '../../ConfirmEx';
import { toast } from '../Toast';
import Toggle from '../Toggle';
import AccountNav from './AccountNav';
import ChangePasswordModal from './ChangePasswordModal';

// UI only for now (user decision 2026-09-24): no api calls. Placeholder data shaped like the planned
// GET /api/profile/security result; the backend is parked in tmp/figma-impl/account-security-backend.patch.
const PLACEHOLDER: AccountSecurityResult = {
    passwordChanged: '2026-03-14T02:00:00Z',
    devices: [
        { id: 'this', userAgent: navigator.userAgent, ip: null, lastUsed: new Date().toISOString(), current: true },
        { id: 'phone', userAgent: 'Mozilla/5.0 (iPhone; CPU iPhone OS 17_5 like Mac OS X) AppleWebKit/605.1.15 (KHTML, like Gecko) Version/17.5 Mobile/15E148 Safari/604.1', ip: '203.0.113.24', lastUsed: moment().subtract(2, 'hours').toISOString(), current: false },
        { id: 'office', userAgent: 'Mozilla/5.0 (Windows NT 10.0; Win64; x64) AppleWebKit/537.36 (KHTML, like Gecko) Chrome/128.0.0.0 Safari/537.36', ip: '198.51.100.7', lastUsed: '2026-08-31T01:00:00Z', current: false },
    ],
};

// "Chrome on Windows" from the User-Agent; order matters (Edge and Opera also say Chrome, Chrome also says Safari)
const deviceName = (ua: string | null) => {
    if (!ua) return 'Unknown browser';
    const browser = /Edg\//.test(ua) ? 'Edge' : /OPR\//.test(ua) ? 'Opera' : /Firefox\//.test(ua) ? 'Firefox'
        : /Chrome\/|CriOS\//.test(ua) ? 'Chrome' : /Safari\//.test(ua) ? 'Safari' : 'Browser';
    const os = /iPhone/.test(ua) ? 'iPhone' : /iPad/.test(ua) ? 'iPad' : /Android/.test(ua) ? 'Android'
        : /Windows/.test(ua) ? 'Windows' : /Mac OS X|Macintosh/.test(ua) ? 'Mac' : /Linux/.test(ua) ? 'Linux' : '';
    return os ? `${browser} on ${os}` : browser;
};

const lastActive = (d: AccountDeviceView) => {
    const at = moment(d.lastUsed);
    if (d.current || moment().diff(at, 'minutes') < 5) return 'Active now';
    if (moment().diff(at, 'days') < 7) return at.fromNow();
    return at.format('D MMM YYYY');
};

const NOT_CONNECTED = 'Preview only: this isn’t connected yet, so nothing was changed';

const AccountSecurity: React.FC = () => {
    const [security] = useState<AccountSecurityResult>(PLACEHOLDER);
    const [changingPassword, setChangingPassword] = useState(false);

    const signOut = (d: AccountDeviceView) => toast.info(`${NOT_CONNECTED} (${deviceName(d.userAgent)})`);

    const signOutOthers = async () => {
        const ok = await ConfirmEx('Sign out of every device except this one? They will need your password and an email code to sign in again.');
        if (ok) toast.info(NOT_CONNECTED);
    };

    const others = security.devices.filter(d => !d.current);

    return <>
        <div className="page-header">
            <div>
                <h1>Account &amp; Security</h1>
                <p>Keep your account safe. Two‑factor authentication is on.</p>
            </div>
        </div>

        <div className="account-row">
            <AccountNav />

            <div className="account-main">
                <section className="card">
                    <div className="section-title">
                        <span className="icon-tile"><ShieldCheckIcon /></span><h2>Signing In</h2>
                        <span className="badge ok">Protected</span>
                    </div>

                    <div className="setting-row">
                        <div className="text">
                            <div className="title">Password</div>
                            <div className="hint">{security.passwordChanged ? `Last changed ${moment(security.passwordChanged).format('D MMMM YYYY')}` : 'Not changed here yet'}</div>
                        </div>
                        <button type="button" className="btn-outline" onClick={() => setChangingPassword(true)}>Change Password</button>
                    </div>

                    <div className="setting-row">
                        <div className="text">
                            <div className="title">Two‑factor authentication</div>
                            <div className="hint">A 6‑digit code by email each time you sign in on a new device</div>
                        </div>
                        {/* email codes are on for every account and can't be turned off */}
                        <Toggle label="Two‑factor authentication" checked locked />
                    </div>

                    {/* preview only (user decision 2026-09-24): authenticator-app 2FA is on the out-of-scope list in decisions.md;
                        these rows only show the spec until that decision is revisited */}
                    <div className="setting-row">
                        <div className="text">
                            <div className="title">Authenticator app</div>
                            <div className="hint">Use Google Authenticator or 1Password instead of email codes — recommended</div>
                        </div>
                        <button type="button" className="btn-outline" onClick={() => toast.info('Preview only: authenticator apps aren’t available yet')}>Set Up</button>
                    </div>

                    <div className="setting-row">
                        <div className="text">
                            <div className="title">Backup codes</div>
                            <div className="hint">8 unused codes. Keep them somewhere safe.</div>
                        </div>
                        <button type="button" className="btn-outline" onClick={() => toast.info('Preview only: backup codes aren’t available yet')}>View Codes</button>
                    </div>
                </section>

                <section className="card">
                    <div className="section-title">
                        <span className="icon-tile"><FiClock /></span><h2>Where You're Signed In</h2>
                        {others.length > 0 && <button type="button" className="danger-link in-title" onClick={signOutOthers}>Sign out of all other devices</button>}
                    </div>

                    {security.devices.map(d => <div key={d.id} className="setting-row">
                        <div className="text">
                            <div className="title">{deviceName(d.userAgent)}</div>
                            <div className="hint">{[d.ip && `IP ${d.ip}`, d.current && 'This device', lastActive(d)].filter(Boolean).join(' · ')}</div>
                        </div>
                        {d.current
                            ? <span className="badge ok">Current</span>
                            : <button type="button" className="link" onClick={() => signOut(d)}>Sign out</button>}
                    </div>)}
                    {/* mobile: the title is too narrow for it, so it sits under the list instead (Account.scss) */}
                    {others.length > 0 && <button type="button" className="danger-link under-list" onClick={signOutOthers}>Sign out of all other devices</button>}
                </section>
            </div>
        </div>

        {changingPassword && <ChangePasswordModal onClose={() => setChangingPassword(false)} />}
    </>;
};

export default AccountSecurity;
